// Lightweight client-side wishlist store (localStorage backed).
// No backend wiring — purely a UX placeholder.
import { useEffect, useState } from "react";
import { normalizeTcgLabel } from "@/lib/marketplace";

export interface WishlistItem {
  key: string; // unique slug: tcg|name|set|cardNumber
  cardName: string;
  tcg: string;
  set?: string;
  cardNumber?: string;
  addedAt: number;
}

const STORAGE_KEY = "wtb-wishlist-v1";
const EVT = "wtb-wishlist-changed";

function normalizeItem(it: WishlistItem): WishlistItem {
  const tcg = normalizeTcgLabel(it.tcg) || it.tcg;
  if (tcg === it.tcg) return it;
  const next = { ...it, tcg };
  next.key = makeKey({ tcg, cardName: it.cardName, set: it.set, cardNumber: it.cardNumber });
  return next;
}

function read(): WishlistItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw: WishlistItem[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const fixed = raw.map(normalizeItem);
    // Persist repaired labels once if anything changed.
    if (fixed.some((it, i) => it !== raw[i])) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fixed)); } catch {}
    }
    return fixed;
  } catch {
    return [];
  }
}

function write(items: WishlistItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(EVT));
}

export function makeKey(p: { tcg: string; cardName: string; set?: string; cardNumber?: string }) {
  return [p.tcg, p.cardName, p.set ?? "", p.cardNumber ?? ""].join("|").toLowerCase();
}

export function addToWishlist(item: Omit<WishlistItem, "addedAt" | "key"> & { key?: string }) {
  const tcg = normalizeTcgLabel(item.tcg) || item.tcg;
  const normalized = { ...item, tcg };
  const key = item.key ?? makeKey(normalized);
  const list = read();
  if (list.some((x) => x.key === key)) return false;
  list.unshift({ ...normalized, key, addedAt: Date.now() });
  write(list);
  return true;
}

export function removeFromWishlist(key: string) {
  write(read().filter((x) => x.key !== key));
}

export function useWishlist() {
  const [items, setItems] = useState<WishlistItem[]>(() => read());
  useEffect(() => {
    function refresh() { setItems(read()); }
    window.addEventListener(EVT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(EVT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return items;
}

export function useInWishlist(key: string) {
  const items = useWishlist();
  return items.some((x) => x.key === key);
}

// Deprecated — kept as a no-op shim. Use useIsSignedIn() from
// src/lib/auth-context.tsx in React components instead.
export function isSignedIn() {
  return false;
}
